import React, { useState } from 'react';
import { RealTimeMap } from './components/RealTimeMap';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://urbannerve.org';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('customer');
  const [unitNumber, setUnitNumber] = useState('');
  const [hospitalId, setHospitalId] = useState('');

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (data.success) {
        setUser(data.user);
        setToken(data.token);
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
      } else {
        setError(data.message);
      }
    } catch {
      setError('Connection failed');
    }
    setLoading(false);
  };

  const handleRegister = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, phone, role, unitNumber, hospitalId })
      });
      const data = await res.json();
      if (data.success) {
        setUser(data.user);
        setToken(data.token);
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
      } else {
        setError(data.message);
      }
    } catch {
      setError('Connection failed');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const roleIcons: {[key: string]: string} = {
    customer: '🙋',
    ambulance: '🚑',
    hospital: '🏥',
    admin: '⚙️'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.75rem 1rem',
    marginBottom: '0.75rem',
    borderRadius: '0.5rem',
    border: '1px solid rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.07)',
    color: 'white',
    fontSize: '0.875rem',
    boxSizing: 'border-box',
    outline: 'none'
  };

  if (user) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          height: '4rem',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 1.5rem',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.5rem' }}>🚑</span>
            <div>
              <h1 style={{ fontSize: '1.1rem', fontWeight: 'bold', margin: 0 }}>UrbanNerve</h1>
              <p style={{ fontSize: '0.7rem', margin: 0, opacity: 0.8 }}>Emergency Coordination System</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '500' }}>{roleIcons[user.role]} {user.name}</p>
              <p style={{ margin: 0, fontSize: '0.7rem', opacity: 0.8, textTransform: 'uppercase' }}>{user.role} Portal</p>
            </div>
            <button
              onClick={handleLogout}
              style={{
                padding: '0.4rem 0.9rem',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                borderRadius: '0.4rem',
                border: '1px solid rgba(255,255,255,0.3)',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              Logout
            </button>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <RealTimeMap userRole={user.role} token={token} userId={user.id} userName={user.name} />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background decorative circles */}
      <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(59,130,246,0.1)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-150px', left: '-100px', width: '500px', height: '500px', borderRadius: '50%', background: 'rgba(59,130,246,0.08)', pointerEvents: 'none' }} />

      <div style={{
        backgroundColor: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(20px)',
        padding: '2.5rem',
        borderRadius: '1.5rem',
        boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
        maxWidth: '420px',
        width: '90%',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🚑</div>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: '0 0 0.25rem 0' }}>UrbanNerve</h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.875rem' }}>Emergency Coordination System</p>
        </div>

        {/* Toggle */}
        <div style={{
          display: 'flex',
          marginBottom: '1.5rem',
          borderRadius: '0.75rem',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.1)',
          backgroundColor: 'rgba(0,0,0,0.2)'
        }}>
          <button onClick={() => setMode('login')} style={{
            flex: 1, padding: '0.6rem',
            backgroundColor: mode === 'login' ? '#2563eb' : 'transparent',
            color: 'white', border: 'none', cursor: 'pointer',
            fontWeight: '500', fontSize: '0.875rem'
          }}>Login</button>
          <button onClick={() => setMode('register')} style={{
            flex: 1, padding: '0.6rem',
            backgroundColor: mode === 'register' ? '#2563eb' : 'transparent',
            color: 'white', border: 'none', cursor: 'pointer',
            fontWeight: '500', fontSize: '0.875rem'
          }}>Register</button>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'rgba(239,68,68,0.2)',
            border: '1px solid rgba(239,68,68,0.4)',
            color: '#fca5a5',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            fontSize: '0.875rem'
          }}>⚠️ {error}</div>
        )}

        {mode === 'login' ? (
          <div>
            <input type="email" placeholder="Email address" value={email}
              onChange={e => setEmail(e.target.value)} style={inputStyle} />
            <input type="password" placeholder="Password" value={password}
              onChange={e => setPassword(e.target.value)} style={inputStyle}
              onKeyDown={e => e.key === 'Enter' && handleLogin()} />
            <button onClick={handleLogin} disabled={loading} style={{
              width: '100%', padding: '0.75rem',
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: 'white', border: 'none', borderRadius: '0.5rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold', fontSize: '1rem',
              opacity: loading ? 0.7 : 1,
              boxShadow: '0 4px 15px rgba(37,99,235,0.4)'
            }}>
              {loading ? 'Logging in...' : 'Login →'}
            </button>
          </div>
        ) : (
          <div>
            <input type="text" placeholder="Full Name" value={name}
              onChange={e => setName(e.target.value)} style={inputStyle} />
            <input type="email" placeholder="Email address" value={email}
              onChange={e => setEmail(e.target.value)} style={inputStyle} />
            <input type="password" placeholder="Password" value={password}
              onChange={e => setPassword(e.target.value)} style={inputStyle} />
            <input type="tel" placeholder="Phone Number" value={phone}
              onChange={e => setPhone(e.target.value)} style={inputStyle} />
            <select value={role} onChange={e => setRole(e.target.value)} style={{...inputStyle, cursor: 'pointer'}}>
              <option value="customer" style={{background:'#1e3a8a'}}>🙋 Customer</option>
              <option value="ambulance" style={{background:'#1e3a8a'}}>🚑 Ambulance Driver</option>
              <option value="hospital" style={{background:'#1e3a8a'}}>🏥 Hospital Staff</option>
              <option value="admin" style={{background:'#1e3a8a'}}>⚙️ Admin</option>
            </select>
            {role === 'ambulance' && (
              <input type="text" placeholder="Unit Number (e.g. AMB001)" value={unitNumber}
                onChange={e => setUnitNumber(e.target.value)} style={inputStyle} />
            )}
            {role === 'hospital' && (
              <input type="text" placeholder="Hospital ID" value={hospitalId}
                onChange={e => setHospitalId(e.target.value)} style={inputStyle} />
            )}
            <button onClick={handleRegister} disabled={loading} style={{
              width: '100%', padding: '0.75rem',
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: 'white', border: 'none', borderRadius: '0.5rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold', fontSize: '1rem',
              opacity: loading ? 0.7 : 1,
              boxShadow: '0 4px 15px rgba(37,99,235,0.4)'
            }}>
              {loading ? 'Registering...' : 'Create Account →'}
            </button>
          </div>
        )}

        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', marginTop: '1.5rem', marginBottom: 0 }}>
          UrbanNerve is a coordination tool. Always call local emergency services in life-threatening situations.
        </p>
      </div>
    </div>
  );
}

export default App;